import { z } from 'zod/v4';

export const SEND_TO_CMM_MAX_LINES = 100;

/**
 * `cmm.orders.buyer_official_name` is `varchar(255)` and the order-hub alias
 * column is `text`, so the length is checked here rather than left to fail
 * mid-send.
 */
export const CMM_BUYER_OFFICIAL_NAME_MAX_LENGTH = 255;

/**
 * Only the selection. The tracing twin's `notifySupplier` and
 * `attachmentTemplateId` have no CMM meaning: a CMM record has no attachment
 * template, and the send must never notify the counterparty.
 */
export const SendOrderToCmmInputSchema = z.object({
  orderLineIds: z
    .array(z.string().min(1))
    .min(1)
    .max(SEND_TO_CMM_MAX_LINES)
    .optional(),
});

export type SendOrderToCmmInput = z.infer<typeof SendOrderToCmmInputSchema>;

export const SendOrderToCmmLineResultSchema = z.object({
  orderLineId: z.string(),
  orderLineNumber: z.string(),
  cmmOrderLineId: z.string(),
  cmmRequestedAt: z.string(),
});

export type SendOrderToCmmLineResult = z.infer<
  typeof SendOrderToCmmLineResultSchema
>;

export const SendOrderToCmmSkippedLineSchema = z.object({
  orderLineId: z.string(),
  orderLineNumber: z.string().nullable(),
  reasons: z.array(z.string()),
});

export type SendOrderToCmmSkippedLine = z.infer<
  typeof SendOrderToCmmSkippedLineSchema
>;

/**
 * No `cmmOrderId`. The gateway pre-generates one per call, but a re-send
 * upserts onto the record the first send created, so the pre-generated id is
 * not necessarily the record's id. The per-line ids are always fresh inserts
 * and are safe to return.
 */
export const SendOrderToCmmResponseSchema = z.object({
  orderId: z.string(),
  results: z.array(SendOrderToCmmLineResultSchema),
  skippedLines: z.array(SendOrderToCmmSkippedLineSchema),
});

export type SendOrderToCmmResponse = z.infer<
  typeof SendOrderToCmmResponseSchema
>;

/**
 * Whether the CMM ↔ Order Hub integration is open for the caller's tenant: the
 * launch gate plus the brand CMM and Order Hub features. The interface reads it
 * so the CMM column ships dark on exactly the tenants the send route refuses.
 */
export const CmmIntegrationStatusSchema = z.object({
  enabled: z.boolean(),
});

export type CmmIntegrationStatus = z.infer<typeof CmmIntegrationStatusSchema>;
